export interface CalendarHolidayInfo {
    date: string;
    isWeekend: boolean;
    isPublicHoliday: boolean;
    isHoliday: boolean;
    isSubstitute: boolean;
    names: string[];
    badge: string;
    title: string;
    ariaLabel: string;
    className: string;
}

interface HolidayRule {
    month: number;
    day: number;
    name: string;
    substitute: boolean;
}

interface HolidayEntry {
    date: string;
    name: string;
    substitute: boolean;
    substituteGroup: string;
    isSubstitute?: boolean;
    substituteFor?: string[];
}

const SOLAR_HOLIDAY_RULES: HolidayRule[] = [
    { month: 1, day: 1, name: "신정", substitute: false },
    { month: 3, day: 1, name: "삼일절", substitute: true },
    { month: 5, day: 5, name: "어린이날", substitute: true },
    { month: 6, day: 6, name: "현충일", substitute: false },
    { month: 8, day: 15, name: "광복절", substitute: true },
    { month: 10, day: 3, name: "개천절", substitute: true },
    { month: 10, day: 9, name: "한글날", substitute: true },
    { month: 12, day: 25, name: "성탄절", substitute: true }
];

const LUNAR_HOLIDAY_DATES: any = {
    2020: { seollal: ["2020-01-24", "2020-01-25", "2020-01-26"], chuseok: ["2020-09-30", "2020-10-01", "2020-10-02"], buddha: "2020-04-30" },
    2021: { seollal: ["2021-02-11", "2021-02-12", "2021-02-13"], chuseok: ["2021-09-20", "2021-09-21", "2021-09-22"], buddha: "2021-05-19" },
    2022: { seollal: ["2022-01-31", "2022-02-01", "2022-02-02"], chuseok: ["2022-09-09", "2022-09-10", "2022-09-11"], buddha: "2022-05-08" },
    2023: { seollal: ["2023-01-21", "2023-01-22", "2023-01-23"], chuseok: ["2023-09-28", "2023-09-29", "2023-09-30"], buddha: "2023-05-27" },
    2024: { seollal: ["2024-02-09", "2024-02-10", "2024-02-11"], chuseok: ["2024-09-16", "2024-09-17", "2024-09-18"], buddha: "2024-05-15" },
    2025: { seollal: ["2025-01-28", "2025-01-29", "2025-01-30"], chuseok: ["2025-10-05", "2025-10-06", "2025-10-07"], buddha: "2025-05-05" },
    2026: { seollal: ["2026-02-16", "2026-02-17", "2026-02-18"], chuseok: ["2026-09-24", "2026-09-25", "2026-09-26"], buddha: "2026-05-24" },
    2027: { seollal: ["2027-02-06", "2027-02-07", "2027-02-08"], chuseok: ["2027-09-14", "2027-09-15", "2027-09-16"], buddha: "2027-05-13" },
    2028: { seollal: ["2028-01-26", "2028-01-27", "2028-01-28"], chuseok: ["2028-10-02", "2028-10-03", "2028-10-04"], buddha: "2028-05-02" },
    2029: { seollal: ["2029-02-12", "2029-02-13", "2029-02-14"], chuseok: ["2029-09-21", "2029-09-22", "2029-09-23"], buddha: "2029-05-20" },
    2030: { seollal: ["2030-02-02", "2030-02-03", "2030-02-04"], chuseok: ["2030-09-11", "2030-09-12", "2030-09-13"], buddha: "2030-05-09" },
    2031: { seollal: ["2031-01-22", "2031-01-23", "2031-01-24"], chuseok: ["2031-09-30", "2031-10-01", "2031-10-02"], buddha: "2031-05-28" },
    2032: { seollal: ["2032-02-10", "2032-02-11", "2032-02-12"], chuseok: ["2032-09-18", "2032-09-19", "2032-09-20"], buddha: "2032-05-16" },
    2033: { seollal: ["2033-01-30", "2033-01-31", "2033-02-01"], chuseok: ["2033-09-07", "2033-09-08", "2033-09-09"], buddha: "2033-05-06" },
    2034: { seollal: ["2034-02-18", "2034-02-19", "2034-02-20"], chuseok: ["2034-09-26", "2034-09-27", "2034-09-28"], buddha: "2034-05-25" },
    2035: { seollal: ["2035-02-07", "2035-02-08", "2035-02-09"], chuseok: ["2035-09-15", "2035-09-16", "2035-09-17"], buddha: "2035-05-15" }
};

const holidayEntriesByYear: any = {};
const holidayInfoByDate: any = {};

export function getCalendarHolidayInfo(value: string | Date): CalendarHolidayInfo {
    const dateKey = value instanceof Date ? formatDateKey(value) : String(value || "");
    if (holidayInfoByDate[dateKey]) return holidayInfoByDate[dateKey];

    const date = parseDateKey(dateKey);
    if (!date) return emptyHolidayInfo(dateKey);

    const publicHolidayEntries = getKoreanPublicHolidayEntries(dateKey);
    const isWeekend = isWeekendDate(dateKey);
    const isPublicHoliday = publicHolidayEntries.length > 0;
    const isSubstitute = publicHolidayEntries.some(entry => !!entry.isSubstitute);
    const names = uniqueValues(publicHolidayEntries.map(entry => entry.name));
    const weekdayName = date.getDay() === 0 ? "일요일" : (date.getDay() === 6 ? "토요일" : "");
    const badge = createHolidayBadge(names, isWeekend, isSubstitute);
    const titleParts = names.length > 0 ? names : (weekdayName ? [weekdayName] : []);
    const classNames: string[] = [];

    if (isWeekend) classNames.push("is-weekend");
    if (isWeekend || isPublicHoliday) classNames.push("is-holiday");
    if (isPublicHoliday) classNames.push("is-public-holiday");
    if (isSubstitute) classNames.push("is-substitute-holiday");

    const info = {
        date: dateKey,
        isWeekend,
        isPublicHoliday,
        isHoliday: isWeekend || isPublicHoliday,
        isSubstitute,
        names,
        badge,
        title: titleParts.join(", "),
        ariaLabel: titleParts.length > 0 ? `${dateKey} ${titleParts.join(", ")}` : dateKey,
        className: classNames.join(" ")
    };

    holidayInfoByDate[dateKey] = info;
    return info;
}

export function getKoreanPublicHolidayEntries(dateKey: string): HolidayEntry[] {
    const date = parseDateKey(dateKey);
    if (!date) return [];
    const yearEntries = getKoreanPublicHolidays(date.getFullYear());
    return yearEntries.filter(entry => entry.date === dateKey);
}

export function getKoreanPublicHolidays(year: number): HolidayEntry[] {
    if (holidayEntriesByYear[year]) return holidayEntriesByYear[year];

    const entries = createBaseHolidayEntries(year);
    const holidayMap = createHolidayMap(entries);
    const substituteTriggers = getSubstituteTriggerDates(holidayMap);

    substituteTriggers.forEach(trigger => {
        const substituteDate = findNextOpenHolidayDate(addDaysKey(trigger.date, 1), holidayMap);
        const names = uniqueValues(trigger.entries.map(entry => entry.name));
        addHolidayEntry(holidayMap, {
            date: substituteDate,
            name: `${names[0]} 대체공휴일`,
            substitute: false,
            substituteGroup: "substitute",
            isSubstitute: true,
            substituteFor: names
        });
    });

    holidayEntriesByYear[year] = flattenHolidayMap(holidayMap).sort((a, b) => a.date.localeCompare(b.date));
    return holidayEntriesByYear[year];
}

function createBaseHolidayEntries(year: number): HolidayEntry[] {
    const entries: HolidayEntry[] = SOLAR_HOLIDAY_RULES.map(rule => ({
        date: createDateKey(year, rule.month, rule.day),
        name: rule.name,
        substitute: rule.substitute,
        substituteGroup: rule.name
    }));

    const lunarDates = LUNAR_HOLIDAY_DATES[year];
    if (!lunarDates) return entries;

    lunarDates.seollal.forEach(date => {
        entries.push({
            date,
            name: "설날",
            substitute: true,
            substituteGroup: "seollal"
        });
    });

    lunarDates.chuseok.forEach(date => {
        entries.push({
            date,
            name: "추석",
            substitute: true,
            substituteGroup: "chuseok"
        });
    });

    entries.push({
        date: lunarDates.buddha,
        name: "부처님오신날",
        substitute: true,
        substituteGroup: "부처님오신날"
    });

    return entries;
}

function getSubstituteTriggerDates(holidayMap: any) {
    return Object.keys(holidayMap)
        .sort()
        .filter(dateKey => isSubstituteTriggerDate(dateKey, holidayMap[dateKey]))
        .map(dateKey => ({ date: dateKey, entries: holidayMap[dateKey].filter(entry => entry.substitute) }));
}

function isSubstituteTriggerDate(dateKey: string, entries: HolidayEntry[]) {
    const substituteEntries = entries.filter(entry => entry.substitute);
    if (substituteEntries.length === 0) return false;

    const hasLunarPeriod = substituteEntries.some(entry => entry.substituteGroup === "seollal" || entry.substituteGroup === "chuseok");
    const hasSingleDayHoliday = substituteEntries.some(entry => entry.substituteGroup !== "seollal" && entry.substituteGroup !== "chuseok");
    const isSundayHoliday = getDayOfWeek(dateKey) === 0;
    const isSaturdayHoliday = getDayOfWeek(dateKey) === 6;
    const hasOverlap = entries.length > 1;

    if (hasSingleDayHoliday && (isSaturdayHoliday || isSundayHoliday || hasOverlap)) return true;
    return hasLunarPeriod && (isSundayHoliday || hasOverlap);
}

function findNextOpenHolidayDate(startDateKey: string, holidayMap: any) {
    let candidate = startDateKey;
    while (holidayMap[candidate] || isWeekendDate(candidate)) {
        candidate = addDaysKey(candidate, 1);
    }
    return candidate;
}

function createHolidayMap(entries: HolidayEntry[]) {
    const holidayMap: any = {};
    entries.forEach(entry => addHolidayEntry(holidayMap, entry));
    return holidayMap;
}

function addHolidayEntry(holidayMap: any, entry: HolidayEntry) {
    if (!holidayMap[entry.date]) holidayMap[entry.date] = [];
    holidayMap[entry.date].push(entry);
}

function flattenHolidayMap(holidayMap: any) {
    return Object.keys(holidayMap).reduce((entries: HolidayEntry[], dateKey: string) => {
        return entries.concat(holidayMap[dateKey]);
    }, []);
}

function createHolidayBadge(names: string[], isWeekend: boolean, isSubstitute: boolean) {
    if (isSubstitute) return "대체휴일";
    if (names.length > 1) return "공휴일";
    if (names.length === 1) return names[0];
    return isWeekend ? "주말" : "";
}

function isWeekendDate(dateKey: string) {
    const day = getDayOfWeek(dateKey);
    return day === 0 || day === 6;
}

function getDayOfWeek(dateKey: string) {
    const date = parseDateKey(dateKey);
    return date ? date.getDay() : -1;
}

function parseDateKey(dateKey: string) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
    const parts = dateKey.split("-").map(part => parseInt(part, 10));
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return formatDateKey(date) === dateKey ? date : null;
}

function createDateKey(year: number, month: number, day: number) {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDateKey(date: Date) {
    return createDateKey(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function addDaysKey(dateKey: string, amount: number) {
    const date = parseDateKey(dateKey);
    if (!date) return dateKey;
    return formatDateKey(new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount));
}

function uniqueValues(values: string[]) {
    const seen: any = {};
    return values.filter(value => {
        const key = String(value || "");
        if (!key || seen[key]) return false;
        seen[key] = true;
        return true;
    });
}

function emptyHolidayInfo(dateKey: string): CalendarHolidayInfo {
    return {
        date: dateKey,
        isWeekend: false,
        isPublicHoliday: false,
        isHoliday: false,
        isSubstitute: false,
        names: [],
        badge: "",
        title: "",
        ariaLabel: dateKey,
        className: ""
    };
}
